import React, { useState } from 'react';
import { useCurrency, CURRENCY_MAP } from './CurrencyState';

/**
 * CurrencySelector.jsx
 * Highly customizable currency selector component with 4 display variants:
 * - 'dropdown' (HTML select / custom dropdown)
 * - 'pills' (Horizontal button pills)
 * - 'flags' (Flag + Code badges)
 * - 'minimal' (Clean text links)
 *
 * Supports stroke/border, hover colors, padding, corner radius, font size, and layout position.
 */
export const CurrencySelector = ({ settings = {}, style = {}, className = '' }) => {
    const { enabled, activeCurrency, setActiveCurrency, enabledCurrencies } = useCurrency();
    const [isHovered, setIsHovered] = useState(false);
    const [hoveredCurr, setHoveredCurr] = useState(null);

    if (
        settings.hideCurrencySelector ||
        settings.currencySelectorDisabled ||
        settings.hideHeaderCurrencySelector ||
        !enabled ||
        !enabledCurrencies ||
        enabledCurrencies.length <= 1
    ) {
        return null;
    }

    // Read customizer settings
    const variant = settings.currencySelectorVariant || 'dropdown'; // 'dropdown' | 'pills' | 'flags' | 'minimal'
    const placement = settings.currencySelectorPlacement || 'header-right'; // 'header-right' | 'header-left' | 'nav-inline' | 'footer' | 'floating'

    // Style tokens
    const bg = isHovered ? (settings.currencySelectorHoverBg || settings.currencySelectorBg || '#f8fafc') : (settings.currencySelectorBg || '#f8fafc');
    const color = isHovered ? (settings.currencySelectorHoverColor || settings.currencySelectorColor || '#1e293b') : (settings.currencySelectorColor || '#1e293b');
    const border = settings.currencySelectorBorder || '1px solid #cbd5e1';
    const hoverBorder = settings.currencySelectorHoverBorder || border;
    const radius = settings.currencySelectorRadius || '8px';
    const padding = settings.currencySelectorPadding || '4px 10px';
    const fontSize = settings.currencySelectorFontSize || '0.85rem';
    const fontWeight = settings.currencySelectorFontWeight || '600';
    const fontFamily = (settings.currencySelectorFontFamily && settings.currencySelectorFontFamily !== 'inherit') ? settings.currencySelectorFontFamily : undefined;
    const shadow = settings.currencySelectorShadow || settings.currencySelectorBoxShadow || undefined;
    const showFlags = settings.currencyShowFlags !== false && settings.currencyShowFlags !== 'false';

    // Base container container style based on placement
    let wrapperStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', ...style };
    if (placement === 'floating') {
        wrapperStyle = {
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 9999,
            boxShadow: shadow || '0 8px 24px rgba(0,0,0,0.18)',
            background: settings.currencySelectorBg || '#ffffff',
            borderRadius: radius,
            padding: '4px',
            ...style
        };
    }

    // Variant 1: HTML Select / Dropdown
    if (variant === 'dropdown') {
        return (
            <div className={`currency-selector-wrapper currency-pos-${placement} ${className}`} style={wrapperStyle}>
                <select
                    value={activeCurrency}
                    onChange={(e) => setActiveCurrency(e.target.value.toUpperCase())}
                    onMouseOver={() => setIsHovered(true)}
                    onMouseOut={() => setIsHovered(false)}
                    style={{
                        background: bg,
                        color: color,
                        border: isHovered ? hoverBorder : border,
                        borderRadius: radius,
                        padding: padding,
                        fontSize: fontSize,
                        fontWeight: fontWeight,
                        fontFamily: fontFamily,
                        boxShadow: shadow,
                        cursor: 'pointer',
                        outline: 'none',
                        transition: 'all 0.15s ease-in-out',
                        boxSizing: 'border-box'
                    }}
                >
                    {enabledCurrencies.map(currCode => {
                        const info = CURRENCY_MAP[currCode] || { symbol: '$', name: currCode, flag: '' };
                        const label = showFlags ? `${info.flag} ${currCode} (${info.symbol})` : `${currCode} (${info.symbol})`;
                        return (
                            <option key={currCode} value={currCode}>
                                {label}
                            </option>
                        );
                    })}
                </select>
            </div>
        );
    }

    // Variant 2: Horizontal Pills
    if (variant === 'pills') {
        return (
            <div className={`currency-selector-wrapper currency-pos-${placement} ${className}`} style={{ ...wrapperStyle, gap: '4px' }}>
                {enabledCurrencies.map(currCode => {
                    const info = CURRENCY_MAP[currCode] || { symbol: '$', name: currCode, flag: '' };
                    const active = activeCurrency === currCode;
                    const isCurrHovered = hoveredCurr === currCode;

                    const itemBg = active
                        ? (settings.currencySelectorActiveBg || settings.primaryColor || '#3b82f6')
                        : (isCurrHovered ? (settings.currencySelectorHoverBg || '#f1f5f9') : bg);

                    const itemColor = active
                        ? (settings.currencySelectorActiveColor || '#ffffff')
                        : (isCurrHovered ? (settings.currencySelectorHoverColor || '#0f172a') : color);

                    return (
                        <button
                            key={currCode}
                            type="button"
                            onClick={() => setActiveCurrency(currCode)}
                            onMouseOver={() => setHoveredCurr(currCode)}
                            onMouseOut={() => setHoveredCurr(null)}
                            style={{
                                background: itemBg,
                                color: itemColor,
                                border: active ? `2px solid ${settings.primaryColor || '#3b82f6'}` : border,
                                borderRadius: radius,
                                padding: padding,
                                fontSize: fontSize,
                                fontWeight: active ? 'bold' : fontWeight,
                                fontFamily: fontFamily,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease-in-out',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                lineHeight: 1
                            }}
                        >
                            {showFlags && info.flag && <span>{info.flag}</span>}
                            <span>{currCode}</span>
                        </button>
                    );
                })}
            </div>
        );
    }

    // Variant 3: Flag + Code Badges
    if (variant === 'flags') {
        return (
            <div className={`currency-selector-wrapper currency-pos-${placement} ${className}`} style={{ ...wrapperStyle, gap: '6px' }}>
                {enabledCurrencies.map(currCode => {
                    const info = CURRENCY_MAP[currCode] || { symbol: '$', name: currCode, flag: '' };
                    const active = activeCurrency === currCode;

                    return (
                        <button
                            key={currCode}
                            type="button"
                            onClick={() => setActiveCurrency(currCode)}
                            title={`${info.name} (${info.symbol})`}
                            style={{
                                background: active ? (settings.currencySelectorActiveBg || '#eff6ff') : bg,
                                color: active ? (settings.currencySelectorActiveColor || '#1d4ed8') : color,
                                border: active ? `2px solid ${settings.primaryColor || '#3b82f6'}` : border,
                                borderRadius: radius,
                                padding: padding,
                                fontSize: fontSize,
                                fontWeight: active ? 'bold' : fontWeight,
                                fontFamily: fontFamily,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                boxShadow: active ? '0 2px 6px rgba(59,130,246,0.2)' : 'none',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            <span style={{ fontSize: '1.1em' }}>{info.flag || '🌐'}</span>
                            <span>{currCode}</span>
                        </button>
                    );
                })}
            </div>
        );
    }

    // Variant 4: Minimal Links
    return (
        <div className={`currency-selector-wrapper currency-pos-${placement} ${className}`} style={{ ...wrapperStyle, gap: '8px' }}>
            {enabledCurrencies.map((currCode, idx) => {
                const active = activeCurrency === currCode;
                return (
                    <React.Fragment key={currCode}>
                        {idx > 0 && <span style={{ color: '#cbd5e1', fontSize: fontSize }}>|</span>}
                        <button
                            type="button"
                            onClick={() => setActiveCurrency(currCode)}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: active ? (settings.primaryColor || '#3b82f6') : color,
                                fontWeight: active ? 'bold' : fontWeight,
                                textDecoration: active ? 'underline' : 'none',
                                fontSize: fontSize,
                                fontFamily: fontFamily,
                                cursor: 'pointer',
                                padding: 0
                            }}
                        >
                            {currCode}
                        </button>
                    </React.Fragment>
                );
            })}
        </div>
    );
};
